import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import FacultyDetails from './components/FacultyDetails';
import Teachers from './components/Teachers';
import AdminSubunits from './components/AdminSubunits';
import AdminStaffs from './components/AdminStaffs';
import ServiceSubunits from './components/ServiceSubunits';
import ServiceStaffs from './components/ServiceStaffs';
import LiveLogs from './components/LiveLogs';
import api, { clearStoredAuth } from './utils/api';
import './App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const bootstrapAuth = async () => {
      const params = new URLSearchParams(window.location.search);
      const redirectPath = params.get('redirect');

      if (redirectPath) {
        sessionStorage.setItem('pstu_diary_redirect_after_login', redirectPath);
      }

      const token = localStorage.getItem('pstu_diary_token');

      if (!token) {
        clearStoredAuth();
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('/auth/verify');

        if (!response.data.success) {
          throw new Error(response.data.message || 'Token validation failed');
        }

        const verifiedUser = response.data.user;
        localStorage.setItem('pstu_diary_user', JSON.stringify(verifiedUser));
        setUser(verifiedUser);
        setIsAuthenticated(true);

        if (redirectPath && window.location.pathname === '/') {
          window.history.replaceState({}, '', redirectPath);
          sessionStorage.removeItem('pstu_diary_redirect_after_login');
        }
      } catch (error) {
        clearStoredAuth();
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    bootstrapAuth();
  }, []);

  const handleLoginSuccess = (userData, token) => {
    setUser(userData);
    setIsAuthenticated(true);

    const redirectPath = sessionStorage.getItem('pstu_diary_redirect_after_login');
    if (redirectPath) {
      sessionStorage.removeItem('pstu_diary_redirect_after_login');
      window.location.replace(redirectPath);
    }
  };

  const handleLogout = () => {
    setUser(null);
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-shell">
          <div className="loading-brand">
            <span className="loading-mark">PD</span>
            <div className="loading-copy">
              <strong>PSTU Diary Admin</strong>
              <span>Checking session and preparing your workspace</span>
            </div>
          </div>
          <div className="loading-visual">
            <div className="loading-spinner"></div>
            <div className="loading-pulse"></div>
          </div>
          <div className="loading-lines">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Router basename="/admin">
      <div className="App">
        <Routes>
          <Route 
            path="/" 
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Login onLoginSuccess={handleLoginSuccess} />
              )
            } 
          />
          <Route 
            path="/dashboard" 
            element={
              isAuthenticated ? (
                <Dashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/faculties/:id" 
            element={
              isAuthenticated ? (
                <FacultyDetails />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/faculties/:facultyId/departments/:departmentId" 
            element={
              isAuthenticated ? (
                <Teachers />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/administrations/:adminUnitId/subunits" 
            element={
              isAuthenticated ? (
                <AdminSubunits />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/administrations/:adminUnitId/subunits/:subunitId/staffs" 
            element={
              isAuthenticated ? (
                <AdminStaffs />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/services/:serviceId/subunits" 
            element={
              isAuthenticated ? (
                <ServiceSubunits />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/services/:serviceId/subunits/:subunitId/staffs" 
            element={
              isAuthenticated ? (
                <ServiceStaffs />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/logs" 
            element={
              isAuthenticated ? (
                <LiveLogs />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
